import { useEffect, useRef, useCallback } from 'react';
import { CHART_TYPES, CHART_CATEGORIES, type ChartTypeDefinition } from '../components/chartTheme';

// ─── Interfaces ───────────────────────────────────────────────────────

export interface ChartAPIFilters {
  sites: string[];
  wells: string[];
  analytes: string[];
  dateFrom: string;
  dateTo: string;
}

export interface ChartAPIParams {
  activeChart: string;
  setActiveChart: (chartType: string) => void;
  filters: ChartAPIFilters;
  setFilters: (filters: Partial<ChartAPIFilters>) => void;
  chartData: any;
}

export interface ChartInsights {
  count: number;
  min: number | null;
  max: number | null;
  mean: number | null;
  exceedances: number;
  trendDirection: 'up' | 'down' | 'flat' | 'unknown';
}

export interface ChartExportRequest {
  format: 'png' | 'svg';
}

export interface SetFiltersInput {
  sites?: string[];
  wells?: string[];
  analytes?: string[];
  dateRange?: { from: string; to: string };
}

export interface ArgosChartAPI {
  getAvailableCharts: () => ChartTypeDefinition[];
  setActiveChart: (chartType: string) => boolean;
  setFilters: (input: SetFiltersInput) => void;
  getChartData: () => any;
  exportChart: (format: 'png' | 'svg') => Promise<void>;
  getChartInsights: () => ChartInsights;
  onChartChange: (cb: (chartType: string) => void) => () => void;
  onFilterChange: (cb: (filters: ChartAPIFilters) => void) => () => void;
  onDataLoad: (cb: (data: any) => void) => () => void;
}

// ─── Window augmentation ──────────────────────────────────────────────

declare global {
  interface Window {
    __ARGOS_CHART_API__?: ArgosChartAPI;
  }
}

// ─── Helper: compute basic stats ──────────────────────────────────────

function computeInsights(data: any): ChartInsights {
  const empty: ChartInsights = {
    count: 0,
    min: null,
    max: null,
    mean: null,
    exceedances: 0,
    trendDirection: 'unknown',
  };

  if (!data) return empty;

  // Normalise to array
  const rows: any[] = Array.isArray(data) ? data : data?.rows ?? data?.data ?? [];
  if (rows.length === 0) return empty;

  // Extract numeric concentration values
  const values: number[] = [];
  let exceedances = 0;

  for (const row of rows) {
    const val =
      row.concentration ??
      row.value ??
      row.result ??
      row.concentration_mg_l ??
      row.y;
    if (val != null && typeof val === 'number' && !isNaN(val)) {
      values.push(val);
    }
    // Count exceedances based on common flag patterns
    if (
      row.exceeds_mcl === true ||
      row.exceedance === true ||
      row.is_exceedance === true
    ) {
      exceedances++;
    }
  }

  if (values.length === 0) return { ...empty, count: rows.length };

  const sorted = [...values].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const mean = values.reduce((s, v) => s + v, 0) / values.length;

  // Simple trend: compare first-third average to last-third average
  let trendDirection: ChartInsights['trendDirection'] = 'unknown';
  if (values.length >= 6) {
    const third = Math.floor(values.length / 3);
    const firstSlice = values.slice(0, third);
    const lastSlice = values.slice(-third);
    const firstAvg = firstSlice.reduce((s, v) => s + v, 0) / firstSlice.length;
    const lastAvg = lastSlice.reduce((s, v) => s + v, 0) / lastSlice.length;
    const pctChange = Math.abs(lastAvg - firstAvg) / (firstAvg || 1);
    if (pctChange < 0.05) {
      trendDirection = 'flat';
    } else {
      trendDirection = lastAvg > firstAvg ? 'up' : 'down';
    }
  }

  return {
    count: rows.length,
    min,
    max,
    mean: parseFloat(mean.toFixed(6)),
    exceedances,
    trendDirection,
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────

export function useChartAPI(params: ChartAPIParams) {
  const paramsRef = useRef(params);
  paramsRef.current = params;

  // Callback registries
  const chartChangeListeners = useRef<Set<(chartType: string) => void>>(new Set());
  const filterChangeListeners = useRef<Set<(filters: ChartAPIFilters) => void>>(new Set());
  const dataLoadListeners = useRef<Set<(data: any) => void>>(new Set());

  // Fire listeners when activeChart changes
  const prevChart = useRef(params.activeChart);
  useEffect(() => {
    if (params.activeChart !== prevChart.current) {
      prevChart.current = params.activeChart;
      chartChangeListeners.current.forEach((cb) => cb(params.activeChart));
    }
  }, [params.activeChart]);

  // Fire listeners when filters change
  const prevFilters = useRef(params.filters);
  useEffect(() => {
    if (params.filters !== prevFilters.current) {
      prevFilters.current = params.filters;
      filterChangeListeners.current.forEach((cb) => cb(params.filters));
    }
  }, [params.filters]);

  // Fire listeners when chartData changes
  const prevData = useRef(params.chartData);
  useEffect(() => {
    if (params.chartData !== prevData.current) {
      prevData.current = params.chartData;
      dataLoadListeners.current.forEach((cb) => cb(params.chartData));
    }
  }, [params.chartData]);

  // Build the API object
  const getAvailableCharts = useCallback((): ChartTypeDefinition[] => {
    return CHART_TYPES;
  }, []);

  const setActiveChart = useCallback((chartType: string): boolean => {
    const exists = CHART_TYPES.some((c) => c.id === chartType);
    if (!exists) return false;
    paramsRef.current.setActiveChart(chartType);
    return true;
  }, []);

  const setFilters = useCallback((input: SetFiltersInput): void => {
    const patch: Partial<ChartAPIFilters> = {};
    if (input.sites) patch.sites = input.sites;
    if (input.wells) patch.wells = input.wells;
    if (input.analytes) patch.analytes = input.analytes;
    if (input.dateRange) {
      patch.dateFrom = input.dateRange.from;
      patch.dateTo = input.dateRange.to;
    }
    paramsRef.current.setFilters(patch);
  }, []);

  const getChartData = useCallback((): any => {
    return paramsRef.current.chartData;
  }, []);

  const exportChart = useCallback((format: 'png' | 'svg'): Promise<void> => {
    return new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Chart export timed out'));
      }, 10000);

      const handler = (e: Event) => {
        const customEvent = e as CustomEvent;
        if (customEvent.detail?.success) {
          clearTimeout(timeout);
          resolve();
        } else {
          clearTimeout(timeout);
          reject(new Error(customEvent.detail?.error ?? 'Export failed'));
        }
      };

      window.addEventListener('argos-export-chart-done', handler, { once: true });
      window.dispatchEvent(
        new CustomEvent('argos-export-chart', { detail: { format } }),
      );
    });
  }, []);

  const getChartInsights = useCallback((): ChartInsights => {
    return computeInsights(paramsRef.current.chartData);
  }, []);

  // Mount / unmount the global API
  useEffect(() => {
    const api: ArgosChartAPI = {
      getAvailableCharts,
      setActiveChart,
      setFilters,
      getChartData,
      exportChart,
      getChartInsights,
      onChartChange: (cb) => {
        chartChangeListeners.current.add(cb);
        return () => { chartChangeListeners.current.delete(cb); };
      },
      onFilterChange: (cb) => {
        filterChangeListeners.current.add(cb);
        return () => { filterChangeListeners.current.delete(cb); };
      },
      onDataLoad: (cb) => {
        dataLoadListeners.current.add(cb);
        return () => { dataLoadListeners.current.delete(cb); };
      },
    };

    window.__ARGOS_CHART_API__ = api;
    // Notify any TERRA action queued before this mount completed.
    window.dispatchEvent(new CustomEvent('argos:chart-api-ready'));

    return () => {
      if (window.__ARGOS_CHART_API__ === api) {
        delete window.__ARGOS_CHART_API__;
      }
      chartChangeListeners.current.clear();
      filterChangeListeners.current.clear();
      dataLoadListeners.current.clear();
    };
  }, [getAvailableCharts, setActiveChart, setFilters, getChartData, exportChart, getChartInsights]);

  return null;
}
